import { useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, TextInput, Textarea } from '@components/ui';
import Modal from '@components/ui/Modal/Modal';
import { createDocumentType, updateDocumentType } from '@/services/documentTypeService';
import { useTranslation } from '@/hooks/useTranslation';
import { useNotificationStore } from '@stores/notificationStore';
import { WorkpayApiError } from '@lib/apiEnvelope';
import type { DocumentTypeDto } from '@/types/documentType';
import styles from './DocumentTypeModals.module.scss';

// ============================================
// TYPES
// ============================================

interface CreateEditDocumentTypeModalProps {
  isOpen: boolean;
  onClose: () => void;
  editData?: DocumentTypeDto | null;
}

interface DocumentTypeFormValues {
  name: string;
  description: string;
}

// ============================================
// DEFAULTS
// ============================================

const DEFAULT_VALUES: DocumentTypeFormValues = {
  name: '',
  description: '',
};

// ============================================
// COMPONENT
// ============================================

function CreateEditDocumentTypeModal({
  isOpen,
  onClose,
  editData,
}: CreateEditDocumentTypeModalProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const addNotification = useNotificationStore((s) => s.addNotification);
  const isEditMode = Boolean(editData);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<DocumentTypeFormValues>({
    defaultValues: DEFAULT_VALUES,
    mode: 'onChange',
  });

  useEffect(() => {
    if (!isOpen) return;
    if (editData) {
      reset({
        name: editData.name,
        description: editData.description ?? '',
      });
    } else {
      reset(DEFAULT_VALUES);
    }
  }, [isOpen, editData, reset]);

  const createMutation = useMutation({
    mutationFn: createDocumentType,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['documentTypes'] });
      reset();
      onClose();
    },
    onError: (error) => {
      const e = error as WorkpayApiError;
      addNotification({ variant: 'critical', title: t(e.description, e.description) });
    },
  });

  const updateMutation = useMutation({
    mutationFn: useCallback(
      (data: DocumentTypeFormValues) => {
        const payload = {
          name: data.name,
          description: data.description || undefined,
        };
        return updateDocumentType(editData!.id, payload);
      },
      [editData],
    ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['documentTypes'] });
      reset();
      onClose();
    },
    onError: (error) => {
      const e = error as WorkpayApiError;
      addNotification({ variant: 'critical', title: t(e.description, e.description) });
    },
  });

  const mutation = isEditMode ? updateMutation : createMutation;

  const onSubmit = useCallback(
    (data: DocumentTypeFormValues) => {
      if (isEditMode) {
        updateMutation.mutate(data);
      } else {
        createMutation.mutate({
          name: data.name,
          description: data.description || undefined,
        });
      }
    },
    [isEditMode, createMutation, updateMutation],
  );

  const handleClose = useCallback(() => {
    if (!mutation.isPending) {
      reset();
      onClose();
    }
  }, [mutation.isPending, reset, onClose]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={isEditMode ? t('K_MODAL_EDIT_DOCUMENT_TYPE', 'Edit Document Type') : t('K_MODAL_CREATE_DOCUMENT_TYPE', 'Create Document Type')}
      size="md"
      footer={
        <div className={styles.formFooter}>
          <Button variant="outline" size="sm" onClick={handleClose} disabled={mutation.isPending}>
            {t('K_CANCEL', 'Cancel')}
          </Button>
          <Button
            variant="filled"
            size="sm"
            type="submit"
            form="document-type-form"
            disabled={mutation.isPending}
          >
            {mutation.isPending ? t('K_BUTTON_SAVING', 'Saving...') : isEditMode ? t('K_UPDATE', 'Update') : t('K_SAVE', 'Save')}
          </Button>
        </div>
      }
    >
      <form id="document-type-form" onSubmit={handleSubmit(onSubmit)} className={styles.form}>
        <TextInput
          label={t('K_LABEL_NAME', 'Name')}
          isRequired
          fullWidth
          placeholder={t('K_PLACEHOLDER_DOCUMENT_TYPE_NAME', 'e.g. Employment Contract')}
          error={errors.name?.message}
          {...register('name', {
            required: t('K_VALIDATION_NAME_REQUIRED', 'Name is required'),
            maxLength: { value: 100, message: t('K_VALIDATION_MAX_100', 'Max 100 characters') },
          })}
        />

        <div className={styles.fullWidth}>
          <Textarea
            label={t('K_LABEL_DESCRIPTION', 'Description')}
            placeholder={t('K_PLACEHOLDER_DESCRIPTION', 'Enter a description')}
            error={errors.description?.message}
            rows={3}
            fullWidth
            {...register('description', {
              maxLength: { value: 500, message: t('K_VALIDATION_MAX_500', 'Max 500 characters') },
            })}
          />
        </div>
      </form>
    </Modal>
  );
}

export default CreateEditDocumentTypeModal;
